<div class="multi-video-player">
  @if (videoUrls?.length) {
    <div
      class="video-wrapper"
      #videoWrapper
      [class.fullscreen]="isFullscreen"
      [class.add-overlay-mode]="isAddingOverlay"
      (click)="onVideoSurfaceClick($event)"
    >
      <video
        #videoA
        [src]="slotAUrl"
        [class.active]="activeSlot === 'A'"
        (timeupdate)="onTimeUpdate('A')"
        (play)="onPlay('A')"
        (pause)="onPause('A')"
        (ended)="onEnded('A')"
        class="video-element slot"
        playsinline
        preload="auto"
      ></video>

      <video
        #videoB
        [src]="slotBUrl"
        [class.active]="activeSlot === 'B'"
        (timeupdate)="onTimeUpdate('B')"
        (play)="onPlay('B')"
        (pause)="onPause('B')"
        (ended)="onEnded('B')"
        class="video-element slot"
        playsinline
        preload="auto"
      ></video>

      <!-- Multi-clip render is still being assembled (probing per-clip
           durations one at a time). Hidden for single-clip playback. -->
      @if (!metadataReady && videoUrls.length > 1) {
        <div class="mvp-prep-overlay">
          <div class="mvp-prep-spinner"></div>
          <div class="mvp-prep-title">Preparing render…</div>
          <div class="mvp-prep-sub">
            Loading clip {{ probedClipCount + 1 }} of {{ videoUrls.length }}
          </div>
        </div>
      }

      <!-- Per-clip unavailability overlay. Sits above the active <video>
           but below the spatial annotation layer so feedback pins stay
           clickable. Pointer events pass through (the overlay is read-only
           — user can still scrub, pause, hit timeline notes). Audio is
           hard-muted in TS via getSlotClipVolume() so we don't need to
           gate it here. -->
      @if (isCurrentClipUnavailable) {
        <div class="mvp-unavailable-overlay" role="status" aria-live="polite">
          <div class="mvp-unavailable-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9"/>
              <path d="M5.5 5.5l13 13"/>
            </svg>
          </div>
          <div class="mvp-unavailable-title">{{ currentClipUnavailableReason }}</div>
          <div class="mvp-unavailable-sub">Audio muted · playback continues</div>
        </div>
      }

      <!-- Per-clip AI Edit job indicators. Compact corner pill — does NOT
           cover the frame, just surfaces the state of the currently-playing
           clip. Error state takes precedence over in-progress when both
           somehow co-occur. -->
      @if (isCurrentClipEditError) {
        <div
          class="mvp-edit-status mvp-edit-status--error"
          role="status"
          aria-live="polite"
          [attr.title]="currentClipEditErrorMessage"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>Edit failed</span>
        </div>
      } @else if (isCurrentClipEditing) {
        <div class="mvp-edit-status mvp-edit-status--editing" role="status" aria-live="polite">
          <span class="mvp-edit-status-dot" aria-hidden="true"></span>
          <span>Editing…</span>
        </div>
      }

      <!-- ─── Drawn content sits in the picture, not in the window.
           In fullscreen the wrapper is the whole screen while the video is
           letterboxed inside it, so anything at `inset: 0` out here is drawn
           across the screen. A vertical card came out landscape. ─── -->
      <div
        class="video-frame"
        [style.left.px]="frameBox.left"
        [style.top.px]="frameBox.top"
        [style.width.px]="frameBox.width"
        [style.height.px]="frameBox.height"
      >
      <!-- ─── Title card: this slot has no video, it is drawn ─── -->
      @if (currentCard; as card) {
        <div
          class="video-card"
          [style.background]="card.backgroundColor || '#000000'"
          [style.color]="card.color || '#FFFFFF'"
          [style.text-align]="card.alignment || 'center'"
          [style.align-items]="card.alignment === 'left' ? 'flex-start' : card.alignment === 'right' ? 'flex-end' : 'center'"
          [style.opacity]="cardAnimStyle(card).opacity"
          [style.transform]="cardAnimStyle(card).transform"
          aria-hidden="true"
        >
          @if (card.backgroundImageUrl) {
            <img
              class="vc-bg"
              [src]="card.backgroundImageUrl"
              [style.object-fit]="cardObjectFit(card)"
              alt=""
            />
          }
          @if (card.logo; as logo) {
            <img
              class="vc-logo"
              [src]="logo.url"
              [style.left.%]="logo.xPct"
              [style.top.%]="logo.yPct"
              [style.width.px]="cardLogoPx(card)"
              [style.opacity]="logo.opacity"
              alt=""
            />
          }
          @if (card.title) {
            <div class="vc-title" [style.font-size.px]="cardTitlePx(card)">{{ card.title }}</div>
          }
          @if (card.subtitle) {
            <div class="vc-sub" [style.font-size.px]="cardTitlePx(card) * 0.45">{{ card.subtitle }}</div>
          }
          @if (card.body) {
            <div class="vc-body" [style.font-size.px]="cardTitlePx(card) * 0.33">{{ card.body }}</div>
          }
          @if (card.cta) {
            <div class="vc-cta" [style.font-size.px]="cardTitlePx(card) * 0.38">{{ card.cta }}</div>
          }
        </div>
      }

      <!-- ─── Fades and transitions dip the picture (spec §10) ───
           Painted imperatively by tickDip() so a half-second fade is
           smooth; text and pins stay above it, as they do in the render. -->
      <div class="video-dim" #videoDim aria-hidden="true"></div>

      <!-- ─── On-screen text (supers, spec §12) ─── -->
      @for (t of visibleTextOverlays; track t.id) {
        <div
          class="video-text"
          [class.boxed]="t.box"
          [class.shadowed]="t.shadow !== false"
          [style.left.%]="t.xPct"
          [style.top.%]="t.yPct"
          [style.max-width.%]="t.widthPct || 80"
          [style.font-size.px]="textPx(t)"
          [style.color]="t.color || '#FFFFFF'"
          [style.text-align]="t.align || 'center'"
          [style.opacity]="textAnimStyle(t).opacity"
          [style.transform]="textAnimStyle(t).transform"
          aria-hidden="true"
        >{{ textContent(t) }}</div>
      }
      </div>
      <!-- ─── end of the picture box ─── -->

      <!-- ─── Spatial overlays drawn over the video frame ─── -->
      @for (overlay of visibleOverlays; track overlay.id) {
        <div
          class="video-overlay"
          [class.circle]="overlay.shape === 'circle'"
          [class.rectangle]="overlay.shape === 'rectangle'"
          [class.arrow]="overlay.shape === 'arrow'"
          [class.dragging]="draggingOverlayId === overlay.id"
          [class.readonly]="!canEdit(overlay)"
          [style.left.%]="overlay.x"
          [style.top.%]="overlay.y"
          [style.--overlay-color]="overlay.color || '#00F0FF'"
          [style.--overlay-size.px]="(overlay.size || 40) * overlayScale"
          (mousedown)="onOverlayMouseDown($event, overlay)"
          (wheel)="onOverlayWheel($event, overlay)"
          (click)="onOverlayClick($event, overlay)"
        >
          @if (overlay.label || overlay.createdByName) {
            <span class="overlay-label">
              @if (overlay.label) {
                <span class="overlay-label-text">{{ overlay.label }}</span>
              }
              @if (overlay.createdByName || overlay.createdAt) {
                <span class="overlay-label-meta">
                  @if (overlay.createdByName) {
                    <span>{{ overlay.createdByName }}</span>
                  }
                  @if (overlay.createdAt) {
                    <span class="overlay-label-sep">·</span>
                    <span>{{ formatOverlayCreatedAt(overlay.createdAt) }}</span>
                  }
                </span>
              }
            </span>
          }
          @if (canEdit(overlay)) {
            <button
              type="button"
              class="overlay-edit-btn"
              title="Edit annotation"
              (mousedown)="$event.stopPropagation()"
              (click)="onOverlayEdit($event, overlay)"
            >
              <svg width="10" height="10" viewBox="0 0 14 14" fill="none"
                stroke="currentColor" stroke-width="1.6" stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M2 12l1-3 6-6 2 2-6 6-3 1z" />
                <path d="M9 3l2 2" />
              </svg>
            </button>
            <button
              type="button"
              class="overlay-delete-btn"
              title="Delete annotation"
              (mousedown)="$event.stopPropagation()"
              (click)="onOverlayDelete($event, overlay)"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <path d="M2 2l6 6M8 2l-6 6" />
              </svg>
            </button>
          }
        </div>
      }

      <!-- Preview marker shown while the creation form is open -->
      @if (showOverlayForm && newOverlay.x !== undefined) {
        <div
          class="video-overlay preview"
          [class.circle]="newOverlay.shape === 'circle'"
          [class.rectangle]="newOverlay.shape === 'rectangle'"
          [class.arrow]="newOverlay.shape === 'arrow'"
          [style.left.%]="newOverlay.x"
          [style.top.%]="newOverlay.y"
          [style.--overlay-color]="newOverlay.color || '#00F0FF'"
          [style.--overlay-size.px]="(newOverlay.size || 50) * overlayScale"
        ></div>
      }

      <!-- Banner while in "click to place" mode -->
      @if (isAddingOverlay && !showOverlayForm) {
        <div class="add-overlay-indicator" (click)="$event.stopPropagation()">
          <span>Click on the video to place an annotation</span>
          <button class="cancel-btn" (click)="cancelOverlayCreation()">
            Cancel
          </button>
        </div>
      }

      <!-- Overlay creation form (compact, dockable, draggable) -->
      @if (showOverlayForm) {
        <div
          class="overlay-form"
          [class.docked]="!formPos"
          [style.left.px]="formPos?.left"
          [style.top.px]="formPos?.top"
          (click)="$event.stopPropagation()"
        >
          <div
            class="form-header"
            (mousedown)="onFormDragStart($event)"
            title="Drag to move"
          >
            <span class="drag-grip" aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <circle cx="2" cy="2" r="1" />
                <circle cx="2" cy="5" r="1" />
                <circle cx="2" cy="8" r="1" />
                <circle cx="5" cy="2" r="1" />
                <circle cx="5" cy="5" r="1" />
                <circle cx="5" cy="8" r="1" />
              </svg>
            </span>
            <span class="form-title">
              {{ editingOverlayId !== null ? "Edit annotation" : "Annotation" }}
            </span>
            <button
              class="close-btn"
              (click)="cancelOverlayCreation()"
              title="Cancel"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="form-body">
            <input
              class="label-input"
              type="text"
              [(ngModel)]="newOverlay.label"
              placeholder="Label…"
            />

            <div class="form-inline">
              <div class="shape-options">
                <button
                  type="button"
                  [class.active]="newOverlay.shape === 'circle'"
                  (click)="pickOverlayShape('circle')"
                  title="Circle"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                  </svg>
                </button>
                <button
                  type="button"
                  [class.active]="newOverlay.shape === 'rectangle'"
                  (click)="pickOverlayShape('rectangle')"
                  title="Rectangle"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                  </svg>
                </button>
                <button
                  type="button"
                  [class.active]="newOverlay.shape === 'arrow'"
                  (click)="pickOverlayShape('arrow')"
                  title="Arrow"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </button>
              </div>

              <div class="color-options">
                @for (c of overlayColors; track c) {
                  <button
                    type="button"
                    class="color-btn"
                    [class.active]="newOverlay.color === c"
                    [style.background]="c"
                    (click)="pickOverlayColor(c)"
                  ></button>
                }
              </div>
            </div>

            <div class="form-inline timing">
              <label>From</label>
              <input
                type="number"
                [(ngModel)]="newOverlay.startTime"
                min="0"
                [max]="totalDuration"
              />
              <label>To</label>
              <input
                type="number"
                [(ngModel)]="newOverlay.endTime"
                min="0"
                [max]="totalDuration"
              />
              <span class="suffix">s</span>
            </div>

            <div class="form-inline size">
              <label>Size</label>
              <input
                type="range"
                [(ngModel)]="newOverlay.size"
                min="20"
                max="150"
              />
              <span class="size-value">{{ newOverlay.size }}px</span>
            </div>
          </div>

          <div class="form-footer">
            <button class="btn-cancel" (click)="cancelOverlayCreation()">
              Cancel
            </button>
            <button class="btn-save" (click)="saveOverlay()">{{ editingOverlayId !== null ? "Update" : "Save" }}</button>
          </div>
        </div>
      }

      @for (vo of voRuntimes; track $index) {
        <audio
          #voAudio
          [src]="vo.track.url"
          preload="auto"
          class="vo-audio"
          aria-hidden="true"
        ></audio>
      }

      @for (bgm of bgmRuntimes; track $index) {
        <audio
          #bgmAudio
          [src]="bgm.track.url"
          preload="auto"
          class="vo-audio"
          aria-hidden="true"
        ></audio>
      }

      <div class="video-controls-overlay">
        <div class="controls-content">
          <button class="ctrl-btn" (click)="skip(-5)" title="Back 5s">⏪</button>
          <button class="play-btn" (click)="togglePlay()">
            {{ isPlaying ? "⏸" : "▶" }}
          </button>
          <button class="ctrl-btn" (click)="skip(5)" title="Forward 5s">⏩</button>
          <button class="ctrl-btn" (click)="stop()" title="Stop">⏹</button>

          <div class="progress-bar" (click)="seek($event)">
            <div class="progress-fill" [style.width.%]="progress"></div>
            @if (showTimelineMarkers) {
              @for (offset of cumulativeOffsets; track $index; let i = $index) {
                @if (i > 0 && totalDuration > 0) {
                  <div
                    class="clip-marker"
                    [style.left.%]="(offset / totalDuration) * 100"
                  ></div>
                }
              }
              @for (vo of voRuntimes; track $index) {
                @if (totalDuration > 0 && vo.endTime > vo.track.startTime) {
                  <div
                    class="vo-marker"
                    [class.active]="vo.active"
                    [style.left.%]="(vo.track.startTime / totalDuration) * 100"
                    [style.width.%]="
                      ((vo.endTime - vo.track.startTime) / totalDuration) * 100
                    "
                    [title]="
                      'VO ' +
                      formatTime(vo.track.startTime) +
                      ' – ' +
                      formatTime(vo.endTime)
                    "
                  ></div>
                }
              }
              @for (a of annotations; track a.id) {
                @if (totalDuration > 0) {
                  <button
                    type="button"
                    class="annotation-marker"
                    [style.left.%]="getAnnotationPercent(a)"
                    [style.--annotation-color]="a.color || '#FF9E1B'"
                    [title]="a.label"
                    (click)="onAnnotationClick($event, a)"
                  ></button>
                }
              }
            }
          </div>

          <span class="time-display">
            {{ formatTime(currentTime) }} / {{ formatTime(totalDuration) }}
          </span>
          <span class="clip-indicator">
            Clip {{ currentIndex + 1 }}/{{ videoUrls.length }}
          </span>

          <div class="volume-group">
            <button
              class="ctrl-btn"
              (click)="toggleMute()"
              [title]="isMuted ? 'Unmute' : 'Mute'"
            >
              {{ volumeIcon }}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              [value]="masterVolume"
              (input)="onVolumeInput($event)"
              class="volume-slider"
              [style.--volume-pct.%]="masterVolume * 100"
              [title]="'Volume ' + (masterVolume * 100 | number: '1.0-0') + '%'"
            />
          </div>

          <button
            class="ctrl-btn rate-btn"
            (click)="cyclePlaybackRate()"
            title="Playback speed"
          >
            {{ playbackRate }}x
          </button>

          <!-- Timeline-marker toggle. Off by default so the seeker stays
               clean; flip on to surface clip boundaries, VO windows, and
               feedback annotation pins. Keyboard shortcut: M. -->
          <button
            class="ctrl-btn markers-btn"
            [class.active]="showTimelineMarkers"
            (click)="toggleTimelineMarkers()"
            [title]="showTimelineMarkers ? 'Hide timeline markers (M)' : 'Show timeline markers (M)'"
            aria-label="Toggle timeline markers"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="2" y1="8" x2="14" y2="8"></line>
              <line x1="4" y1="5" x2="4" y2="11"></line>
              <line x1="8" y1="5" x2="8" y2="11"></line>
              <line x1="12" y1="5" x2="12" y2="11"></line>
            </svg>
          </button>

          @if (!readOnly) {
          <button
            class="ctrl-btn annotate-btn"
            [class.active]="isAddingOverlay"
            (click)="toggleAddOverlayMode(); $event.stopPropagation()"
            [title]="
              isAddingOverlay
                ? 'Cancel annotation'
                : 'Annotate (click on the video to drop a pin)'
            "
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="8" cy="8" r="3"></circle>
              <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2"></path>
            </svg>
          </button>
          }

          <button
            class="ctrl-btn"
            (click)="toggleFullscreen()"
            [title]="isFullscreen ? 'Exit fullscreen' : 'Fullscreen'"
          >
            {{ isFullscreen ? "⤡" : "⤢" }}
          </button>
        </div>
      </div>
    </div>
  } @else {
    <div class="video-placeholder">
      <span>No videos provided</span>
    </div>
  }
</div>
